import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, FileText, Users, Check, Trash2, Plus, Loader2, Star, Shield } from 'lucide-react';
import { billingManager } from '../../business/billing/BillingManager';
import { tierLabel } from '../../business/billing/featureCatalog';
import { TEAM_MEMBER_ROLE, isTeamMemberOnly } from '../../business/billing/starterSeats';
import { bookingFormManager } from '../../business/booking/BookingFormManager';
import { userManager } from '../../business/users/UserManager';
import type { TeamMember } from '../../business/users/UserManager';
import { useAuth } from '../../contexts/AuthContext';
import { useTeamMembers } from '../../contexts/TenantDataContext';
import { Routemize_Constants_BookingFormStatus } from '../../api/models/Routemize_Constants_BookingFormStatus';
import { Routemize_Constants_SubscriptionTier } from '../../api/models/Routemize_Constants_SubscriptionTier';
import type { Routemize_DTOs_Billing_DowngradeBlockerDto } from '../../api/models/Routemize_DTOs_Billing_DowngradeBlockerDto';
import type { Routemize_DTOs_BookingFormDto } from '../../api/models/Routemize_DTOs_BookingFormDto';

// $39/mo per additional user seat — matches the backend (AdditionalUserPrice = 39m) and the
// pricing shown elsewhere in the billing UI.
const SEAT_PRICE = 39;

type Tier = 'Solo' | 'Pro' | 'Scale';

interface DowngradeBlockerResolverProps {
  /** The tier the tenant is trying to switch to (used for re-validation and labels). */
  targetTier: Tier;
  /** Blockers returned by the pre-flight validation. */
  blockers: Routemize_DTOs_Billing_DowngradeBlockerDto[];
  /** "Keep my current plan" — abandon the downgrade and return to plan selection. */
  onCancel: () => void;
  /** All blockers cleared — proceed with the tier change. */
  onResolved: () => void;
}

/**
 * Inline resolver shown when a downgrade is blocked because the tenant exceeds the target tier's
 * limits. Instead of a dead-end error, it lets the user fix each blocker without leaving the modal:
 *  - Booking forms over the limit  → archive forms to fit (archived forms don't count).
 *  - Users over the included seats  → either keep everyone and pay for add-on seats ($39/mo each),
 *                                     or remove team members to fit.
 *  - Starter roles (Roles)          → Starter's second seat is Team Member only: add seats, or switch
 *                                     members to Team Member.
 * After each action it re-validates against the backend (the source of truth) and, once every
 * blocker clears, enables "Continue".
 */
const DowngradeBlockerResolver: React.FC<DowngradeBlockerResolverProps> = ({
  targetTier,
  blockers: initialBlockers,
  onCancel,
  onResolved,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.profile?.sub;
  const { rawTeamMembers, refreshTeamMembers } = useTeamMembers();

  const [blockers, setBlockers] = useState<Routemize_DTOs_Billing_DowngradeBlockerDto[]>(initialBlockers);
  const [revalidating, setRevalidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Forms state
  const [forms, setForms] = useState<Routemize_DTOs_BookingFormDto[] | null>(null);
  const [selectedFormIds, setSelectedFormIds] = useState<Set<string>>(new Set());
  const [archiving, setArchiving] = useState(false);

  // Users state
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [addingSeats, setAddingSeats] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Roles state
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);

  const formsBlocker = blockers.find(b => b.resource === 'BookingForms');
  const usersBlocker = blockers.find(b => b.resource === 'Users');
  const rolesBlocker = blockers.find(b => b.resource === 'Roles');
  const elevatedMembers = rawTeamMembers.filter(m => !isTeamMemberOnly(m.roleNames ?? []));
  const allClear = blockers.length === 0;

  const revalidate = useCallback(async () => {
    setRevalidating(true);
    setError(null);
    try {
      const result = await billingManager.validateTierChange(
        targetTier as Routemize_Constants_SubscriptionTier
      );
      const next = result.blockers ?? [];
      setBlockers(next);
      return next;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to re-check your plan. Please try again.');
      return blockers;
    } finally {
      setRevalidating(false);
    }
  }, [targetTier, blockers]);

  // Load the booking forms once, when a forms blocker is present.
  useEffect(() => {
    if (formsBlocker && forms === null) {
      bookingFormManager.getAll()
        .then(all => setForms(all.filter(f => f.status !== Routemize_Constants_BookingFormStatus.ARCHIVED)))
        .catch(() => setError('Failed to load your booking forms.'));
    }
  }, [formsBlocker, forms]);

  // Load team members once, when a users blocker is present.
  useEffect(() => {
    if (usersBlocker && members === null) {
      userManager.getTeamMembers()
        .then(setMembers)
        .catch(() => setError('Failed to load your team members.'));
    }
  }, [usersBlocker, members]);

  const toggleForm = (id: string) => {
    setSelectedFormIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const archiveSelected = async () => {
    if (selectedFormIds.size === 0) return;
    setArchiving(true);
    setError(null);
    try {
      for (const id of selectedFormIds) {
        await bookingFormManager.archive(id);
      }
      // Drop archived forms from the local list and re-check against the backend.
      setForms(prev => (prev ? prev.filter(f => !selectedFormIds.has(f.id!)) : prev));
      setSelectedFormIds(new Set());
      await revalidate();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to archive booking form(s).');
    } finally {
      setArchiving(false);
    }
  };

  const addSeatsAndKeep = async (seatsNeeded: number) => {
    if (seatsNeeded <= 0) return;
    setAddingSeats(true);
    setError(null);
    try {
      for (let i = 0; i < seatsNeeded; i++) {
        await billingManager.addUserSeat();
      }
      billingManager.invalidateSubscriptionCache();
      await revalidate();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add seat(s).');
    } finally {
      setAddingSeats(false);
    }
  };

  const makeTeamMember = async (id: string) => {
    setChangingRoleId(id);
    setError(null);
    try {
      await userManager.updateUserRoles(id, [TEAM_MEMBER_ROLE]);
      await refreshTeamMembers();
      await revalidate();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to change the role.');
    } finally {
      setChangingRoleId(null);
    }
  };

  const removeMember = async (id: string) => {
    setRemovingId(id);
    setError(null);
    try {
      await userManager.deleteUser(id);
      billingManager.invalidateSubscriptionCache();
      setMembers(prev => (prev ? prev.filter(m => m.id !== id) : prev));
      setConfirmRemoveId(null);
      await revalidate();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to remove team member.');
    } finally {
      setRemovingId(null);
    }
  };

  const seatsNeeded = usersBlocker?.mustRemove ?? 0;
  const busy = revalidating || archiving || addingSeats || removingId !== null || changingRoleId !== null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold text-amber-900">A few things exceed the {tierLabel(targetTier)} plan</h3>
            <p className="text-sm text-amber-800 mt-1">
              Clear the items below and you can switch to {tierLabel(targetTier)}. Nothing changes to your plan until you continue.
            </p>
          </div>
        </div>
      </div>

      {/* Booking forms blocker */}
      {formsBlocker && (
        <div className="border border-ink-200 rounded-xl p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-ink-900">Booking forms</h4>
              <p className="text-sm text-ink-500">
                {tierLabel(targetTier)} includes {formsBlocker.newLimit}. You have {formsBlocker.currentCount} active —
                archive {formsBlocker.mustRemove} to fit. Archived forms can be restored anytime.
              </p>
            </div>
          </div>

          {forms === null ? (
            <div className="flex items-center gap-2 text-sm text-ink-500 py-4 px-1">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your forms…
            </div>
          ) : (
            <>
              <div className="max-h-56 overflow-y-auto rounded-lg border border-ink-100 divide-y divide-ink-100">
                {forms.map(form => {
                  const id = form.id!;
                  const selected = selectedFormIds.has(id);
                  return (
                    <label
                      key={id}
                      className={`flex items-center gap-3 px-3 py-2.5 text-sm ${
                        form.isDefault ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-ink-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-ink-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                        checked={selected}
                        disabled={form.isDefault || archiving}
                        onChange={() => toggleForm(id)}
                      />
                      <span className="flex-1 font-medium text-ink-900 truncate">{form.formName || 'Untitled form'}</span>
                      {form.isDefault && (
                        <span className="flex items-center gap-1 text-xs text-ink-400">
                          <Star className="w-3 h-3" /> Default · can't archive
                        </span>
                      )}
                      <span className="text-xs text-ink-400">{form.status}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm text-ink-500">
                  {selectedFormIds.size} selected · need {formsBlocker.mustRemove}
                </span>
                {/* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */}
                <button
                  type="button"
                  onClick={archiveSelected}
                  disabled={selectedFormIds.size === 0 || archiving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Archive {selectedFormIds.size > 0 ? selectedFormIds.size : ''} form{selectedFormIds.size === 1 ? '' : 's'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Users blocker */}
      {usersBlocker && (
        <div className="border border-ink-200 rounded-xl p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-ink-900">Team members</h4>
              <p className="text-sm text-ink-500">
                {targetTier === 'Solo'
                  ? `${tierLabel(targetTier)} includes 1 user + 1 Team Member.`
                  : `${tierLabel(targetTier)} includes ${usersBlocker.newLimit} seat${usersBlocker.newLimit === 1 ? '' : 's'}.`}
                {' '}You have {usersBlocker.currentCount} active users.
              </p>
            </div>
          </div>

          {/* Primary, encouraged path: keep everyone, pay for the extra seats */}
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 mb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-emerald-900">Keep your whole team</p>
                <p className="text-sm text-emerald-700 mt-0.5">
                  Add {seatsNeeded} seat{seatsNeeded === 1 ? '' : 's'} at ${SEAT_PRICE}/mo each
                  <span className="font-semibold"> (+${seatsNeeded * SEAT_PRICE}/mo)</span> and everyone stays.
                </p>
              </div>
              {/* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */}
              <button
                type="button"
                onClick={() => addSeatsAndKeep(seatsNeeded)}
                disabled={busy}
                className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                {addingSeats ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Keep everyone
              </button>
            </div>
          </div>

          {/* Secondary path: remove members to fit */}
          <p className="text-sm text-ink-500 mb-2">Or remove {usersBlocker.mustRemove} member{usersBlocker.mustRemove === 1 ? '' : 's'} to fit:</p>
          {members === null ? (
            <div className="flex items-center gap-2 text-sm text-ink-500 py-3 px-1">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your team…
            </div>
          ) : (
            <div className="rounded-lg border border-ink-100 divide-y divide-ink-100">
              {members.map(member => {
                const isSelf = member.id === currentUserId;
                const confirming = confirmRemoveId === member.id;
                return (
                  <div key={member.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                    <div className="w-7 h-7 rounded-full bg-ink-100 flex items-center justify-center text-xs font-semibold text-ink-600 flex-shrink-0">
                      {member.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-ink-900 truncate">{member.name}</div>
                      {member.email && <div className="text-xs text-ink-400 truncate">{member.email}</div>}
                    </div>
                    {isSelf ? (
                      <span className="text-xs text-ink-400">You</span>
                    ) : confirming ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600">Remove permanently?</span>
                        {/* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */}
                        <button
                          type="button"
                          onClick={() => removeMember(member.id)}
                          disabled={removingId !== null}
                          className="px-2.5 py-1 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                        >
                          {removingId === member.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                          Confirm
                        </button>
                        {/* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */}
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveId(null)}
                          disabled={removingId !== null}
                          className="px-2.5 py-1 bg-ink-100 text-ink-600 rounded-md text-xs font-medium hover:bg-ink-200 disabled:opacity-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      /* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */
                      <button
                        type="button"
                        onClick={() => setConfirmRemoveId(member.id)}
                        disabled={busy}
                        className="px-2.5 py-1 text-red-600 rounded-md text-xs font-medium hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center gap-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Starter roles blocker */}
      {rolesBlocker && (
        <div className="border border-ink-200 rounded-xl p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-ink-900">Team member roles</h4>
              <p className="text-sm text-ink-500">
                {tierLabel(targetTier)} includes 1 user + 1 Team Member. {rolesBlocker.currentCount} users have
                other roles.
              </p>
            </div>
          </div>

          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 mb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-emerald-900">Keep everyone's roles</p>
                <p className="text-sm text-emerald-700 mt-0.5">
                  Add {rolesBlocker.mustRemove} seat{rolesBlocker.mustRemove === 1 ? '' : 's'} at ${SEAT_PRICE}/mo each
                  <span className="font-semibold"> (+${(rolesBlocker.mustRemove ?? 0) * SEAT_PRICE}/mo)</span>.
                </p>
              </div>
              {/* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */}
              <button
                type="button"
                onClick={() => addSeatsAndKeep(rolesBlocker.mustRemove ?? 0)}
                disabled={busy}
                className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                {addingSeats ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Keep roles
              </button>
            </div>
          </div>

          <p className="text-sm text-ink-500 mb-2">
            Or switch {rolesBlocker.mustRemove} member{rolesBlocker.mustRemove === 1 ? '' : 's'} to Team Member:
          </p>
          <div className="rounded-lg border border-ink-100 divide-y divide-ink-100">
            {elevatedMembers.map(member => (
              <div key={member.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-ink-900 truncate">{member.name}</div>
                  <div className="text-xs text-ink-400 truncate">{(member.roleNames ?? []).join(', ')}</div>
                </div>
                {member.id === currentUserId ? (
                  <span className="text-xs text-ink-400">You</span>
                ) : (
                  /* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */
                  <button
                    type="button"
                    onClick={() => makeTeamMember(member.id!)}
                    disabled={busy}
                    className="px-2.5 py-1 text-blue-600 rounded-md text-xs font-medium hover:bg-blue-50 disabled:opacity-50 transition-colors flex items-center gap-1"
                  >
                    {changingRoleId === member.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Make Team Member
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Footer */}
      <div className="flex gap-3">
        {/* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */}
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 bg-ink-100 text-ink-700 py-2.5 px-4 rounded-xl font-medium hover:bg-ink-200 disabled:opacity-50 transition-colors"
        >
          Keep my current plan
        </button>
        {/* eslint-disable-next-line no-restricted-syntax -- DowngradeBlockerResolver: billing action buttons match the ManageSubscriptionModal styling */}
        <button
          type="button"
          onClick={onResolved}
          disabled={!allClear || busy}
          className="flex-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white py-2.5 px-4 rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50 disabled:hover:shadow-none flex items-center justify-center gap-2"
        >
          {revalidating ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Re-checking…</>
          ) : allClear ? (
            <><Check className="w-4 h-4" /> Continue to {tierLabel(targetTier)}</>
          ) : (
            'Resolve the items above'
          )}
        </button>
      </div>
    </div>
  );
};

export default DowngradeBlockerResolver;
